import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, ChevronRight } from "lucide-react";
import { theme, card, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { LoadingBlock } from "../../components/Spinner";
import { RegisterToolbar, SummaryTiles } from "../../components/RegisterToolbar";
import { by, downloadCsv, matchesSearch, type SortOption } from "../../lib/registerExport";
import { fmtDay, secondaryBtn } from "./shared";

// "Coming up" on the resilience overview: every dated obligation across the registers in the next
// 30 or 90 days, plus the open ones already overdue (GET /resilience/upcoming, rules in
// backend/src/lib/resilienceUpcoming.ts). Grouped by when it falls; the tiles filter the list.

export type UpcomingKind =
  | "exercise" | "insurance_renewal" | "contract_notice" | "contract_end" | "asset_support_end"
  | "training_expiry" | "training_due" | "bia_review" | "incident_notification" | "dora_clock";
type Urgency = "overdue" | "due_soon" | "upcoming";
type Bucket = "overdue" | "week" | "month" | "later";

export interface UpcomingItem { id: string; kind: UpcomingKind; date: string; label: string; detail: string | null; href: string; urgency: Urgency; bucket: Bucket }
interface UpcomingResponse { days: number; generatedAt: string; doraMonitoringEnabled: boolean; items: UpcomingItem[] }

// Mirror of backend/src/lib/resilienceUpcoming.ts UPCOMING_KIND_LABELS.
const KIND_LABELS: Record<UpcomingKind, string> = {
  exercise: "Exercise",
  insurance_renewal: "Insurance renewal",
  contract_notice: "Supplier notice deadline",
  contract_end: "Supplier agreement ends",
  asset_support_end: "End of vendor support",
  training_expiry: "Training expires",
  training_due: "Assigned training due",
  bia_review: "Business impact review",
  incident_notification: "Incident notification",
  dora_clock: "DORA clock",
};

const BUCKETS: Array<{ id: Bucket; label: string }> = [
  { id: "overdue", label: "Overdue" },
  { id: "week", label: "This week" },
  { id: "month", label: "Next 30 days" },
  { id: "later", label: "Later" },
];

const URGENCY_STYLE: Record<Urgency, { bg: string; color: string; bar: string }> = {
  overdue:  { bg: theme.color.dangerBg,  color: theme.color.dangerText,  bar: theme.color.danger },
  due_soon: { bg: theme.color.warningBg, color: theme.color.warningText, bar: theme.color.warning },
  upcoming: { bg: theme.color.panelAlt,  color: theme.color.muted,       bar: theme.color.borderStrong },
};

const SORTS: SortOption<UpcomingItem>[] = [
  { key: "date", label: "Date", compare: by.date((i) => i.date, "asc") },
  { key: "kind", label: "Type", compare: (a, b) => KIND_LABELS[a.kind].localeCompare(KIND_LABELS[b.kind]) || a.date.localeCompare(b.date) },
  { key: "label", label: "Name", compare: by.text((i) => i.label) },
];

export function OverviewUpcoming() {
  const [days, setDays] = useState<30 | 90>(90);
  const [data, setData] = useState<UpcomingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<Bucket | null>(null);
  const [kind, setKind] = useState<UpcomingKind | "">("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date");

  const load = useCallback(async (d: number) => {
    try { setData(await api.get<UpcomingResponse>(`/resilience/upcoming?days=${d}`)); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load what is coming up")); }
  }, []);
  useEffect(() => { void load(days); }, [load, days]);

  const items = data?.items ?? [];
  const shown = useMemo(() => {
    const cmp = SORTS.find((s) => s.key === sort)?.compare ?? SORTS[0].compare;
    return items
      .filter((i) => (!bucket || i.bucket === bucket) && (!kind || i.kind === kind) && matchesSearch(search, i.label, i.detail, KIND_LABELS[i.kind]))
      .sort(cmp);
  }, [items, bucket, kind, search, sort]);
  const count = (b: Bucket) => items.filter((i) => i.bucket === b).length;
  const kindsPresent = Array.from(new Set(items.map((i) => i.kind)));

  function exportCsv() {
    downloadCsv(`resilience-coming-up-${days}d`, [
      { header: "Date", value: (i) => i.date.slice(0, 10) },
      { header: "When", value: (i) => BUCKETS.find((b) => b.id === i.bucket)?.label ?? i.bucket },
      { header: "Type", value: (i) => KIND_LABELS[i.kind] },
      { header: "Item", value: (i) => i.label },
      { header: "Detail", value: (i) => i.detail },
    ], shown);
  }

  return (
    <div style={{ ...card, padding: 0 }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <CalendarClock size={15} style={{ color: theme.color.heading }} />
        <span style={{ ...text.sectionTitle, margin: 0 }}>Coming up</span>
        <span style={text.hint}>exercises, renewals, notice deadlines, end of support, training, reviews and incident clocks</span>
        <span style={{ flex: 1 }} />
        {([30, 90] as const).map((d) => (
          <button key={d} type="button" onClick={() => setDays(d)} aria-pressed={days === d} style={{ ...secondaryBtn, background: days === d ? "var(--c-accentBg)" : "transparent", color: days === d ? "var(--c-accentText)" : theme.color.muted, borderColor: days === d ? theme.color.accent : theme.color.borderStrong }}>
            Next {d} days
          </button>
        ))}
      </div>
      <div style={{ padding: "14px 18px 4px" }}>
        {error && <div style={{ ...text.body, color: theme.color.dangerText, marginBottom: 10 }}>{error}</div>}
        {!data && !error ? <LoadingBlock /> : (
          <>
            <SummaryTiles tiles={BUCKETS.map((b) => ({
              key: b.id, label: b.label, value: count(b.id),
              tone: b.id === "overdue" ? (count(b.id) ? "danger" : "neutral") : b.id === "week" ? (count(b.id) ? "warning" : "neutral") : "neutral",
              active: bucket === b.id,
              onClick: () => setBucket((cur) => (cur === b.id ? null : b.id)),
              hint: bucket === b.id ? "Click again to show all" : undefined,
            }))} />
            <RegisterToolbar
              search={search} onSearch={setSearch} placeholder="Search what is coming up"
              sortOptions={SORTS} sort={sort} onSort={setSort}
              count={{ shown: shown.length, total: items.length }} onExport={exportCsv}
            >
              {kindsPresent.length > 1 && (
                <select value={kind} onChange={(e) => setKind(e.target.value as UpcomingKind | "")} aria-label="Filter by type" style={{ fontSize: text.hint.fontSize, padding: "7px 10px", borderRadius: 3, border: `1px solid ${theme.color.borderStrong}`, background: theme.color.panel, color: theme.color.text, fontFamily: theme.font }}>
                  <option value="">All types</option>
                  {kindsPresent.sort((a, b) => KIND_LABELS[a].localeCompare(KIND_LABELS[b])).map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
                </select>
              )}
              {(bucket || kind) && <button type="button" style={secondaryBtn} onClick={() => { setBucket(null); setKind(""); }}>Clear filters</button>}
            </RegisterToolbar>
          </>
        )}
      </div>
      {data && (items.length === 0 ? (
        <div style={{ ...text.body, padding: "6px 18px 18px", color: theme.color.muted }}>Nothing dated in the next {days} days, and nothing overdue.</div>
      ) : shown.length === 0 ? (
        <div style={{ ...text.body, padding: "6px 18px 18px", color: theme.color.muted }}>Nothing matches these filters.</div>
      ) : (
        <div>
          {BUCKETS.filter((b) => shown.some((i) => i.bucket === b.id)).map((b) => (
            <div key={b.id}>
              <div style={{ ...text.eyebrow, padding: "8px 18px", color: b.id === "overdue" ? theme.color.dangerText : theme.color.subtle, background: theme.color.panelAlt, borderTop: "1px solid var(--c-border)" }}>
                {b.label} <span style={{ fontWeight: 400 }}>({shown.filter((i) => i.bucket === b.id).length})</span>
              </div>
              {shown.filter((i) => i.bucket === b.id).map((i) => {
                const s = URGENCY_STYLE[i.urgency];
                return (
                  <PortalLink key={i.id} to={i.href} style={{ textDecoration: "none" }}>
                    <div style={{ display: "flex", gap: 12, padding: "10px 18px", borderTop: "1px solid var(--c-border)", alignItems: "center", borderLeft: `3px solid ${s.bar}`, flexWrap: "wrap" }}>
                      <span style={{ ...text.hint, fontWeight: 600, color: s.color, minWidth: 86, whiteSpace: "nowrap" }}>{fmtDay(i.date)}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{KIND_LABELS[i.kind]}</span>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={text.itemTitle}>{i.label}</div>
                        {i.detail && <div style={{ ...text.hint, marginTop: 2 }}>{i.detail}</div>}
                      </div>
                      <ChevronRight size={14} style={{ color: theme.color.faint, flexShrink: 0 }} />
                    </div>
                  </PortalLink>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
